/* =============================================================================
   SmileAura — theme.js
   Vanilla, no dependencies, loaded with `defer`.

   Cart mutations always ask Shopify to re-render the cart-drawer section and
   swap the returned markup in, so money formatting, discounts and totals stay
   Liquid-authoritative. Drawer interactions use event delegation because that
   markup is replaced on every update.
   ========================================================================== */
(function () {
  'use strict';

  var SA = window.SA || {};
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ----------------------------- helpers ---------------------------------- */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function on(el, ev, fn, opts) { if (el) el.addEventListener(ev, fn, opts); }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* Focus trap for the drawers */
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  function trapFocus(container, e) {
    var items = $$(FOCUSABLE, container).filter(function (el) { return el.offsetParent !== null; });
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ========================== 1. ANNOUNCEMENT BAR ========================= */
  function initAnnouncement() {
    $$('[data-announce]').forEach(function (root) {
      var slides = $$('[data-announce-slide]', root);
      if (slides.length < 2) return;

      var i = 0;
      var timer = null;
      var speed = parseInt(root.getAttribute('data-speed'), 10) || 4000;
      var autoplay = root.getAttribute('data-autoplay') === 'true' && !reduceMotion;

      function show(n) {
        i = (n + slides.length) % slides.length;
        slides.forEach(function (s, k) {
          var active = k === i;
          s.classList.toggle('is-active', active);
          if (active) s.removeAttribute('aria-hidden');
          else s.setAttribute('aria-hidden', 'true');
        });
      }
      function start() { if (autoplay && !timer) timer = setInterval(function () { show(i + 1); }, speed); }
      function stop() { clearInterval(timer); timer = null; }

      on(root, 'mouseenter', stop);
      on(root, 'mouseleave', start);
      on(root, 'focusin', stop);
      on(root, 'focusout', start);
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) stop(); else start();
      });

      start();
    });
  }

  /* ============================ 2. HEADER ================================= */
  function initHeader() {
    var header = $('[data-header]');
    var stickyTop = $('[data-sticky-top]');

    /* Shadow once scrolled past the sticky bar */
    if (header && stickyTop) {
      var onScroll = function () {
        header.classList.toggle('is-stuck', window.scrollY > 4);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }

    /* Search panel */
    var searchPanel = $('[data-search-panel]');
    var searchOpen = $('[data-search-open]');
    var searchClose = $('[data-search-close]');
    var searchInput = $('[data-search-input]');

    function toggleSearch(open) {
      if (!searchPanel) return;
      searchPanel.hidden = !open;
      if (searchOpen) searchOpen.setAttribute('aria-expanded', String(open));
      if (open && searchInput) searchInput.focus();
      else if (searchOpen) searchOpen.focus();
    }
    on(searchOpen, 'click', function () { toggleSearch(searchPanel.hidden); });
    on(searchClose, 'click', function () { toggleSearch(false); });

    /* Mobile slide-out nav */
    var drawer = $('[data-drawer]');
    var drawerOpenBtn = $('[data-drawer-open]');
    var lastFocus = null;

    function openDrawer() {
      if (!drawer) return;
      lastFocus = document.activeElement;
      document.body.classList.add('sa-drawer-open');
      if (drawerOpenBtn) drawerOpenBtn.setAttribute('aria-expanded', 'true');
      var first = $(FOCUSABLE, drawer);
      if (first) first.focus();
    }
    function closeDrawer() {
      document.body.classList.remove('sa-drawer-open');
      if (drawerOpenBtn) drawerOpenBtn.setAttribute('aria-expanded', 'false');
      if (lastFocus) lastFocus.focus();
    }

    on(drawerOpenBtn, 'click', openDrawer);
    $$('[data-drawer-close]').forEach(function (b) { on(b, 'click', closeDrawer); });
    on($('[data-drawer-overlay]'), 'click', closeDrawer);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        if (document.body.classList.contains('sa-drawer-open')) closeDrawer();
        else if (searchPanel && !searchPanel.hidden) toggleSearch(false);
      }
      if (e.key === 'Tab' && document.body.classList.contains('sa-drawer-open')) {
        trapFocus(drawer, e);
      }
    });
  }

  /* ========================== 3. CART DRAWER ============================== */
  var Cart = {
    section: null,
    lastFocus: null,

    init: function () {
      this.section = document.getElementById('shopify-section-cart-drawer');
      var self = this;

      $$('[data-cart-open]').forEach(function (btn) {
        on(btn, 'click', function (e) { e.preventDefault(); self.open(); });
      });

      /* Delegated: the drawer markup is replaced on every cart update. */
      if (this.section) {
        on(this.section, 'click', function (e) {
          if (e.target.closest('[data-cart-close]')) { self.close(); return; }
          if (e.target.closest('[data-cart-overlay]')) { self.close(); return; }

          var minus = e.target.closest('[data-cart-minus]');
          var plus = e.target.closest('[data-cart-plus]');
          var remove = e.target.closest('[data-cart-remove]');
          var line = e.target.closest('[data-cart-line]');
          if (!line) return;

          var index = line.getAttribute('data-cart-line');
          var input = $('[data-cart-qty-input]', line);
          var qty = parseInt(input && input.value, 10) || 0;

          if (minus) self.change(index, Math.max(0, qty - 1));
          else if (plus) self.change(index, qty + 1);
          else if (remove) self.change(index, 0);
        });

        on(this.section, 'change', function (e) {
          var input = e.target.closest('[data-cart-qty-input]');
          if (!input) return;
          var line = input.closest('[data-cart-line]');
          if (!line) return;
          var v = parseInt(input.value, 10);
          if (isNaN(v) || v < 0) v = 0;
          self.change(line.getAttribute('data-cart-line'), v);
        });
      }

      document.addEventListener('keydown', function (e) {
        if (!document.body.classList.contains('sa-cart-open')) return;
        if (e.key === 'Escape') self.close();
        if (e.key === 'Tab') trapFocus($('[data-cart-drawer]'), e);
      });
    },

    open: function () {
      this.lastFocus = document.activeElement;
      document.body.classList.add('sa-cart-open');
      var drawer = $('[data-cart-drawer]');
      if (drawer) {
        drawer.removeAttribute('aria-hidden');
        var first = $(FOCUSABLE, drawer);
        if (first) first.focus();
      }
    },

    close: function () {
      document.body.classList.remove('sa-cart-open');
      var drawer = $('[data-cart-drawer]');
      if (drawer) drawer.setAttribute('aria-hidden', 'true');
      if (this.lastFocus && document.contains(this.lastFocus)) this.lastFocus.focus();
    },

    setLoading: function (state) {
      var drawer = $('[data-cart-drawer]');
      if (drawer) drawer.classList.toggle('is-loading', state);
    },

    /* Swap in freshly rendered Liquid and resync the header badge. */
    render: function (html) {
      if (!html || !this.section) return;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.getElementById('shopify-section-cart-drawer') || doc.body.firstElementChild;
      if (!fresh) return;
      this.section.innerHTML = fresh.innerHTML;

      var drawer = $('[data-cart-drawer]');
      var count = drawer ? parseInt(drawer.getAttribute('data-cart-item-count'), 10) : null;
      if (count !== null && !isNaN(count)) this.updateCount(count);
      if (!document.body.classList.contains('sa-cart-open') && drawer) {
        drawer.setAttribute('aria-hidden', 'true');
      }
    },

    /* Header badge only. This writes textContent, so the selector must never be
       able to match a container — scoped to the badge class for that reason. */
    updateCount: function (count) {
      $$('span.sa-cart-count[data-cart-count]').forEach(function (el) {
        el.textContent = count;
        el.classList.toggle('is-empty', count === 0);
        el.classList.add('is-bump');
        setTimeout(function () { el.classList.remove('is-bump'); }, 220);
      });
    },

    change: function (line, quantity) {
      var self = this;
      this.setLoading(true);
      return fetch((SA.cartChangeUrl || '/cart/change') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: quantity, sections: 'cart-drawer' })
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data.sections && data.sections['cart-drawer']) self.render(data.sections['cart-drawer']);
          else window.location.reload();
          if (typeof data.item_count === 'number') self.updateCount(data.item_count);
        })
        .catch(function () { window.location.href = SA.cartUrl || '/cart'; })
        .then(function () { self.setLoading(false); });
    },

    add: function (form, button) {
      var self = this;
      /* Match the label span in either the main or the sticky button. Falling
         back to the button itself would overwrite its inner span with a text
         node, so the sticky bar could never update its price/label again. */
      var textEl = button ? $('[data-atc-text],[data-sticky-atc-text]', button) || button : null;
      var original = textEl ? textEl.textContent : '';

      if (button) { button.classList.add('is-loading'); button.disabled = true; }
      if (textEl) textEl.textContent = (SA.strings && SA.strings.adding) || 'Adding…';

      var body = new FormData(form);
      body.append('sections', 'cart-drawer');

      return fetch((SA.cartAddUrl || '/cart/add') + '.js', {
        method: 'POST',
        headers: { 'Accept': 'application/javascript' },
        body: body
      })
        .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error((res.data && res.data.description) || 'cart error');
          if (res.data.sections && res.data.sections['cart-drawer']) {
            self.render(res.data.sections['cart-drawer']);
          }
          self.open();
        })
        .catch(function (err) {
          /* Fall back to a normal form post so the customer is never stuck. */
          console.warn('[SmileAura] add to cart failed, falling back', err);
          form.submit();
        })
        .then(function () {
          if (button) { button.classList.remove('is-loading'); button.disabled = false; }
          if (textEl) textEl.textContent = original;
        });
    }
  };

  /* ======================= 4. PRODUCT: GALLERY ============================ */
function initGallery() {
  var gallery = $('[data-gallery]');
  if (!gallery) return;

  var allItems = $$('[data-gallery-item]', gallery);
  var allThumbs = $$('[data-gallery-thumb]', gallery);

  var visibleItems = allItems.slice();
  var visibleThumbs = allThumbs.slice();

  function refreshVisible() {
    visibleItems = allItems.filter(function (item) {
      return !item.hidden;
    });

    visibleThumbs = allThumbs.filter(function (thumb) {
      return !thumb.hidden;
    });
  }

  function show(index) {
    refreshVisible();

    if (!visibleItems.length) return;

    if (index < 0) index = 0;
    if (index >= visibleItems.length) index = visibleItems.length - 1;

    visibleItems.forEach(function (item, k) {
      var active = k === index;
      item.classList.toggle('is-active', active);

      if (!active) {
        var video = item.querySelector('video');
        if (video && !video.paused) video.pause();
      }
    });

    visibleThumbs.forEach(function (thumb, k) {
      thumb.classList.toggle('is-active', k === index);
    });
  }

  function filterByVariant(variantId) {
    var id = String(variantId || '');

    allItems.forEach(function (item) {
      var variantMediaId = item.getAttribute('data-variant-media-id') || '';

      /*
       * Media with no variant ID is a general product image.
       * Media with a variant ID only shows for that selected variant.
       */
      var isVariantMedia = variantMediaId !== '';
      var belongsToVariant = variantMediaId.split(/\s+/).indexOf(id) !== -1;

      item.hidden = isVariantMedia && !belongsToVariant;
    });

    allThumbs.forEach(function (thumb) {
      var variantMediaId = thumb.getAttribute('data-variant-media-id') || '';
      var isVariantMedia = variantMediaId !== '';
      var belongsToVariant = variantMediaId.split(/\s+/).indexOf(id) !== -1;

      thumb.hidden = isVariantMedia && !belongsToVariant;
    });

    refreshVisible();

    /*
     * Make the selected colour's featured image the first visible image.
     */
    var selectedIndex = visibleItems.findIndex(function (item) {
      var variantMediaId = item.getAttribute('data-variant-media-id') || '';
      return variantMediaId.split(/\s+/).indexOf(id) !== -1;
    });

    if (selectedIndex === -1) selectedIndex = 0;

    show(selectedIndex);
  }

  allThumbs.forEach(function (thumb) {
    on(thumb, 'click', function () {
      refreshVisible();

      var index = visibleThumbs.indexOf(thumb);

      if (index > -1) {
        show(index);
      }
    });
  });

  /*
   * Exposed so the variant picker can jump to a variant's featured media.
   */
  gallery.showMediaById = function (mediaId) {
    refreshVisible();

    var index = visibleItems.findIndex(function (item) {
      return item.getAttribute('data-media-id') === String(mediaId);
    });

    if (index > -1) {
      show(index);
    }
  };

  /*
   * Exposed so the variant picker can filter the gallery.
   */
  gallery.filterByVariant = filterByVariant;

  /*
   * Start with the currently selected variant.
   */
  var variantData = $('[data-variant-data]');
  if (variantData) {
    try {
      var initialVariants = JSON.parse(variantData.textContent);
      var initialInputs = $$('[data-option-input]', gallery.parentNode || document);

      var selectedOptions = [];

      initialInputs.forEach(function (input) {
        if (input.checked) {
          selectedOptions[parseInt(input.dataset.optionPosition, 10) - 1] = input.value;
        }
      });

      var initialVariant = initialVariants.find(function (variant) {
        return variant.options.every(function (value, index) {
          return selectedOptions[index] === undefined || selectedOptions[index] === value;
        });
      });

      if (initialVariant) {
        filterByVariant(initialVariant.id);
      }
    } catch (e) {
      /* Keep the normal gallery if variant data cannot be read. */
    }
  }
}

  /* ==================== 5. PRODUCT: VARIANT PICKER ======================== */
function initVariants() {
  var form = document.getElementById('sa-product-form');
  var dataEl = document.querySelector('[data-variant-data]');

  if (!form || !dataEl) return;

  var variants;

  try {
    variants = JSON.parse(dataEl.textContent);
  } catch (e) {
    return;
  }

  if (!variants || !variants.length) return;

  var idInput = form.querySelector('[data-variant-id]');
  var inputs = Array.prototype.slice.call(
    form.querySelectorAll('[data-option-input]')
  );

  var priceNow = document.querySelector('[data-price-now]');
  var priceWas = document.querySelector('[data-price-was]');
  var priceSave = document.querySelector('[data-price-save]');
  var galleryFlag = document.querySelector('[data-gallery-flag]');

  var atc = form.querySelector('[data-atc]');
  var atcText = form.querySelector('[data-atc-text]');

  var stickyPrice = document.querySelector('[data-sticky-price]');
  var stickyWas = document.querySelector('[data-sticky-was]');
  var stickyBtn = document.querySelector('[data-sticky-atc-btn]');
  var stickyText = document.querySelector('[data-sticky-atc-text]');

  var gallery = document.querySelector('[data-gallery]');

  function getSelectedOptions() {
    var selected = [];

    inputs.forEach(function(input) {
      if (input.checked) {
        var position = parseInt(input.getAttribute('data-option-position'), 10) - 1;
        selected[position] = input.value;
      }
    });

    return selected;
  }

  function findVariant(options) {
    /* Fix: single-variant products (no option selectors) use their only variant */
    if (!inputs.length) {
      return variants[0];
    }
    return variants.find(function(variant) {
      return variant.options.every(function(value, index) {
        return options[index] === value;
      });
    });
  }

  function updateLabels() {
    inputs.forEach(function(input) {
      var label = input.closest('label');

      if (label) {
        label.classList.toggle('is-active', input.checked);
      }

      if (input.checked) {
        var position = input.getAttribute('data-option-position');
        var selectedText = document.querySelector(
          '[data-option-selected="' + position + '"]'
        );

        if (selectedText) {
          selectedText.textContent = input.value;
        }
      }
    });
  }

  function updateAvailability(options) {
    inputs.forEach(function(input) {
      var position =
        parseInt(input.getAttribute('data-option-position'), 10) - 1;

      var testOptions = options.slice();
      testOptions[position] = input.value;

      var possible = variants.some(function(variant) {
        return variant.available &&
          variant.options.every(function(value, index) {
            return testOptions[index] === undefined ||
                   testOptions[index] === value;
          });
      });

      var label = input.closest('label');

      if (label) {
        label.classList.toggle('is-unavailable', !possible);
      }
    });
  }

  function updateVariant() {
    var options = getSelectedOptions();
    var variant = findVariant(options);

    updateLabels();
    updateAvailability(options);

    if (!variant) {
      if (atc) atc.disabled = true;
      if (stickyBtn) stickyBtn.disabled = true;

      if (atcText) atcText.textContent = 'Unavailable';
      if (stickyText) stickyText.textContent = 'Unavailable';

      return;
    }

    /* Update the actual Shopify variant ID */
    if (idInput) {
      idInput.value = variant.id;
    }

    /* Update price */
    if (priceNow) {
      priceNow.textContent = variant.price;
    }

    if (stickyPrice) {
      stickyPrice.textContent = variant.price;
    }

    if (priceWas) {
      priceWas.textContent = variant.compare_at_price || '';
      priceWas.hidden = !variant.compare_at_price;
    }

    if (stickyWas) {
      stickyWas.textContent = variant.compare_at_price || '';
      stickyWas.hidden = !variant.compare_at_price;
    }

    /* Update savings */
    if (priceSave) {
      priceSave.textContent = variant.save
        ? 'Save ' + variant.save
        : '';

      priceSave.hidden = !variant.save;
    }

    if (galleryFlag) {
      galleryFlag.textContent = variant.save
        ? 'Save ' + variant.save
        : '';

      galleryFlag.hidden = !variant.save;
    }

    /* Update Add To Cart */
    if (atc) {
      atc.disabled = !variant.available;
    }

    if (stickyBtn) {
      stickyBtn.disabled = !variant.available;
    }

    var buttonText = variant.available
      ? ((window.SA && SA.strings && SA.strings.addToCart) || 'Add To Cart')
      : ((window.SA && SA.strings && SA.strings.soldOut) || 'Sold out');

    if (atcText) {
      atcText.textContent = buttonText;
    }

    if (stickyText) {
      stickyText.textContent = buttonText;
    }

    /* Update gallery */
    if (gallery && gallery.filterByVariant) {
      gallery.filterByVariant(variant.id);
    }

    /* Update URL */
    if (window.history && window.history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);

      window.history.replaceState(
        {},
        '',
        url.toString()
      );
    }
  }

  /* Listen for colour / option changes */
  inputs.forEach(function(input) {
    input.addEventListener('change', function() {
      updateVariant();
    });

    input.addEventListener('click', function() {
      /*
       * Ensures the variant updates even if the theme's
       * custom radio styling interferes with the change event.
       */
      setTimeout(updateVariant, 0);
    });
  });

  /* Run once on page load */
  updateVariant();
}
  /* ================== 6. PRODUCT: QTY + ADD TO CART ======================= */
  function initBuy() {
    /* Quantity steppers (product page + anywhere else using [data-qty]) */
    $$('[data-qty]').forEach(function (wrap) {
      var input = $('input', wrap);
      if (!input) return;
      var min = parseInt(input.getAttribute('min'), 10);
      if (isNaN(min)) min = 1;

      function set(v) {
        if (isNaN(v) || v < min) v = min;
        input.value = v;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      on($('[data-qty-minus]', wrap), 'click', function () { set(parseInt(input.value, 10) - 1); });
      on($('[data-qty-plus]', wrap), 'click', function () { set(parseInt(input.value, 10) + 1); });
    });

    /* AJAX add to cart -> open the drawer */
    var form = document.getElementById('sa-product-form');
    if (form) {
      on(form, 'submit', function (e) {
        e.preventDefault();
        Cart.add(form, $('[data-atc]', form));
      });
    }

    /* Sticky mobile ATC mirrors the real form */
    var stickyBtn = $('[data-sticky-atc-btn]');
    if (stickyBtn && form) {
      on(stickyBtn, 'click', function () { Cart.add(form, stickyBtn); });
    }
  }

  /* ========================== 7. STICKY BARS ============================= */
  function initStickyBars() {
    var bar = $('[data-sticky-atc]') || $('[data-sticky-cta]');
    if (!bar) return;

    /* Only reserve space at the bottom of the page when a bar actually renders,
       otherwise every page gets dead space under the footer on mobile. */
    document.body.classList.add('sa-has-sticky');

    /* On the product page, only show once the real buy button is out of view.
       Elsewhere, show once the visitor has scrolled past the hero. */
    var anchor = $('[data-atc]');
    var threshold = anchor ? null : 600;

    function update() {
      var show;
      if (anchor) {
        var r = anchor.getBoundingClientRect();
        show = r.bottom < 0 || r.top > window.innerHeight;
      } else {
        show = window.scrollY > threshold;
      }
      bar.classList.toggle('is-in', show);
      bar.setAttribute('aria-hidden', String(!show));
    }

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  /* ============================ 8. CAROUSEL ============================== */
  function initCarousels() {
    $$('[data-carousel]').forEach(function (root) {
      var track = $('[data-carousel-track]', root);
      var prev = $('[data-carousel-prev]', root);
      var next = $('[data-carousel-next]', root);
      var items = $$('[data-carousel-item]', track);
      var dotsWrap = $('[data-carousel-dots]', root.parentNode) || $('[data-carousel-dots]', root);
      if (!track || !items.length) return;

      function step() {
        var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 20;
        return items[0].offsetWidth + gap;
      }
      function perView() {
        return Math.max(1, Math.round(track.clientWidth / step()));
      }
      function pageCount() {
        return Math.max(1, items.length - perView() + 1);
      }
      function current() {
        return Math.round(track.scrollLeft / step());
      }

      /* Dots */
      var dots = [];
      function buildDots() {
        if (!dotsWrap) return;
        dotsWrap.innerHTML = '';
        dots = [];
        var n = pageCount();
        if (n < 2) return;
        for (var k = 0; k < n; k++) {
          (function (k) {
            var b = document.createElement('button');
            b.type = 'button';
            b.className = 'sa-carousel-dot';
            b.setAttribute('aria-label', 'Go to review ' + (k + 1));
            b.addEventListener('click', function () { track.scrollTo({ left: k * step(), behavior: reduceMotion ? 'auto' : 'smooth' }); });
            dotsWrap.appendChild(b);
            dots.push(b);
          })(k);
        }
      }

      function sync() {
        var i = current();
        dots.forEach(function (d, k) { d.classList.toggle('is-active', k === i); });
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
      }

      on(prev, 'click', function () { track.scrollBy({ left: -step(), behavior: reduceMotion ? 'auto' : 'smooth' }); });
      on(next, 'click', function () { track.scrollBy({ left: step(), behavior: reduceMotion ? 'auto' : 'smooth' }); });
      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', function () { buildDots(); sync(); }, { passive: true });

      buildDots();
      sync();
    });
  }

  /* ======================= 9. BEFORE / AFTER SLIDER ====================== */
  function initBeforeAfter() {
    $$('[data-ba]').forEach(function (stage) {
      var handle = $('[data-ba-handle]', stage);
      if (!handle) return;

      function setPos(p) { stage.style.setProperty('--pos', Math.max(2, Math.min(98, p))); }
      function fromEvent(ev) {
        var r = stage.getBoundingClientRect();
        var cx = ev.clientX != null ? ev.clientX : (ev.touches && ev.touches[0] && ev.touches[0].clientX);
        if (cx != null) setPos((cx - r.left) / r.width * 100);
      }
      function start(e) {
        e.preventDefault();
        function move(ev) { fromEvent(ev); }
        function up() {
          window.removeEventListener('pointermove', move);
          window.removeEventListener('pointerup', up);
        }
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
      }

      on(handle, 'pointerdown', start);
      /* Keyboard support */
      on(handle, 'keydown', function (e) {
        var cur = parseFloat(getComputedStyle(stage).getPropertyValue('--pos')) || 52;
        if (e.key === 'ArrowLeft') { e.preventDefault(); setPos(cur - 4); }
        if (e.key === 'ArrowRight') { e.preventDefault(); setPos(cur + 4); }
      });
    });
  }

  /* ====================== 10. RESULTS GALLERY FILTERS ==================== */
  function initFilters() {
    $$('[data-rg-filters]').forEach(function (bar) {
      var grid = $('[data-rg-grid]');
      if (!grid) return;
      var cards = $$('.sa-rg-card', grid);
      $$('[data-filter]', bar).forEach(function (btn) {
        on(btn, 'click', function () {
          var f = btn.getAttribute('data-filter');
          $$('[data-filter]', bar).forEach(function (b) { b.classList.remove('active'); });
          btn.classList.add('active');
          cards.forEach(function (c) {
            c.classList.toggle('hide', !(f === 'all' || c.getAttribute('data-cat') === f));
          });
        });
      });
    });
  }

  /* =========================== 11. CART PAGE ============================= */
  function initCartPage() {
    var form = $('[data-cart-form]');
    if (!form || !form.querySelector('[data-cart-line]')) return;

    form.addEventListener('click', function (e) {
      var minus = e.target.closest('[data-cart-minus]');
      var plus = e.target.closest('[data-cart-plus]');
      var remove = e.target.closest('[data-cart-remove]');
      if (!minus && !plus && !remove) return;

      var line = e.target.closest('[data-cart-line]');
      if (!line) return;
      e.preventDefault();

      var input = $('[data-cart-qty-input]', line);
      var qty = parseInt(input && input.value, 10) || 0;
      var next = remove ? 0 : (minus ? Math.max(0, qty - 1) : qty + 1);

      fetch((SA.cartChangeUrl || '/cart/change') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: Number(line.getAttribute('data-cart-line')), quantity: next })
      }).then(function () { window.location.reload(); })
        .catch(function () { form.submit(); });
    });
  }

  /* ========================== 12. SCROLL REVEAL ==========================
     Elements start visible. We only hide ("arm") the ones below the fold, and
     only when we are confident the reveal can actually run. IntersectionObserver
     callbacks never fire while a document is hidden (background or prerendered
     tabs), so arming in that state would leave the page blank — bail out and
     show everything instead. Above-the-fold content is never armed at all, so
     the hero can never depend on JS to appear. */
  function initReveal() {
    var els = $$('.sa-reveal');
    if (!els.length) return;

    function showAll() {
      els.forEach(function (el) {
        el.classList.remove('is-armed');
        el.classList.add('is-in');
      });
    }

    if (reduceMotion || !('IntersectionObserver' in window) || document.visibilityState === 'hidden') {
      showAll();
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.remove('is-armed');
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    var fold = window.innerHeight * 0.92;
    els.forEach(function (el) {
      if (el.getBoundingClientRect().top > fold) {
        el.classList.add('is-armed');
        io.observe(el);
      } else {
        el.classList.add('is-in');
      }
    });
  }

  /* ============================== BOOT ================================== */
  ready(function () {
    initAnnouncement();
    initHeader();
    Cart.init();
    initGallery();
    initVariants();
    initBuy();
    initStickyBars();
    initCarousels();
    initBeforeAfter();
    initFilters();
    initCartPage();
    initReveal();
  });

  /* Re-init when the theme editor re-renders a section */
  document.addEventListener('shopify:section:load', function () {
    initAnnouncement();
    initGallery();
    initVariants();
    initBuy();
    initStickyBars();
    initCarousels();
    initBeforeAfter();
    initFilters();
    initReveal();
  });
})();